import type { HousingRtmsBundle } from "./adapters/housingRtms";
export type HousingLoanPurpose = "purchase" | "living" | "all";
export type HousingPolicyImportance = "major" | "minor";
export type HousingPolicyDirection = "tighten" | "ease" | "mixed" | "neutral";
export type HousingPolicyChannel = "mortgage_cap" | "ltv" | "dsr" | "regulated_area" | "funding" | "tax" | "supply" | "other";
export type HousingRegionProfileId = "capital_regulated" | "capital_nonregulated" | "outside_capital_nonregulated";

export interface HousingRegionProfile {
  id: HousingRegionProfileId;
  label: string;
  inCapitalArea: boolean;
  regulatedArea: boolean;
  note: string;
}

export interface HousingPriceBand {
  id: string;
  label: string;
  minPriceExclusive?: number;
  maxPriceInclusive?: number;
  mortgageCapKrw?: number;
}

export interface HousingPolicyRegime {
  id: string;
  announcedAt?: string;
  effectiveFrom: string;
  importance: HousingPolicyImportance;
  direction: HousingPolicyDirection;
  channels: HousingPolicyChannel[];
  effectiveTo?: string;
  appliesToCapitalArea: boolean;
  appliesToRegulatedArea: boolean;
  loanPurpose: HousingLoanPurpose;
  bands: HousingPriceBand[];
  ltvByZone?: {
    regulated?: number;
    nonregulated?: number;
  };
  dsrBank?: number;
  dsrNonBank?: number;
  stressRatePct?: number;
  note: string;
  sourceTitle: string;
  sourceUrl: string;
  reaffirmedAt?: string;
  reaffirmedSourceUrl?: string;
}

export interface MortgageCapacityInput {
  date: string;
  homePriceKrw: number;
  region: HousingRegionProfile;
  loanPurpose?: HousingLoanPurpose;
  dsrLimitKrw?: number | null;
}

export interface MortgageCapacityResult {
  regimeId: string | null;
  bandId: string | null;
  bandLabel: string | null;
  homePriceKrw: number;
  absoluteCapKrw: number | null;
  ltv: number | null;
  ltvLimitKrw: number | null;
  dsrLimitKrw: number | null;
  allowedMortgageKrw: number | null;
  capacityRatio: number | null;
  equityRequiredKrw: number | null;
  bindingConstraint: "absolute_cap" | "ltv" | "dsr" | "multiple" | "unknown";
  note: string;
}

export const HOUSING_REGION_PROFILES: HousingRegionProfile[] = [
  {
    id: "capital_regulated",
    label: "수도권·규제지역(규제 LTV)",
    inCapitalArea: true,
    regulatedArea: true,
    note: "현재 규제지역 일반 LTV 40%를 적용하는 비교 프로필입니다. 실제 개별 차주는 DSR·상품요건 등에 따라 더 낮아질 수 있습니다."
  },
  {
    id: "capital_nonregulated",
    label: "수도권 비규제지역",
    inCapitalArea: true,
    regulatedArea: false,
    note: "가격대별 절대한도는 수도권에도 적용되며, 비규제지역 일반 LTV 70%를 비교용으로 적용합니다."
  },
  {
    id: "outside_capital_nonregulated",
    label: "비수도권 비규제지역",
    inCapitalArea: false,
    regulatedArea: false,
    note: "현재 가격대별 6/4/2억원 절대한도 적용범위 밖의 비교 프로필입니다. LTV만으로 실제 대출액을 단정하지 않습니다."
  }
];

/**
 * Policy regimes are append-only. Historical rules are not overwritten when a new policy starts.
 * Unknown LTV/DSR details are intentionally left undefined rather than backfilled from later rules.
 */
export const HOUSING_POLICY_REGIMES: HousingPolicyRegime[] = [
  {
    id: "fsc-2025-06-28-capital-purchase-cap-600m",
    announcedAt: "2025-06-27",
    effectiveFrom: "2025-06-28",
    importance: "major",
    direction: "tighten",
    channels: ["mortgage_cap", "dsr"],
    effectiveTo: "2025-10-15",
    appliesToCapitalArea: true,
    appliesToRegulatedArea: true,
    loanPurpose: "purchase",
    bands: [{ id: "all", label: "전체 가격대", mortgageCapKrw: 600_000_000 }],
    note: "수도권·규제지역 주택구입목적 주담대 최대한도를 6억원으로 제한. 중도금대출은 제외되며 실제 대출액은 LTV·DTI·DSR 등에 따라 달라집니다. 이 레짐에서는 일반 LTV를 이 파일에서 추정하지 않습니다.",
    sourceTitle: "금융위원회, 수도권 중심 가계부채 관리 강화 방안",
    sourceUrl: "https://www.fsc.go.kr/no010101/84824"
  },
  {
    id: "fsc-2025-10-16-price-tiered-cap",
    announcedAt: "2025-10-15",
    effectiveFrom: "2025-10-16",
    importance: "major",
    direction: "tighten",
    channels: ["mortgage_cap", "ltv", "dsr"],
    appliesToCapitalArea: true,
    appliesToRegulatedArea: true,
    loanPurpose: "purchase",
    bands: [
      { id: "p1_le_15", label: "15억원 이하", maxPriceInclusive: 1_500_000_000, mortgageCapKrw: 600_000_000 },
      { id: "p2_15_25", label: "15억원 초과~25억원 이하", minPriceExclusive: 1_500_000_000, maxPriceInclusive: 2_500_000_000, mortgageCapKrw: 400_000_000 },
      { id: "p3_gt_25", label: "25억원 초과", minPriceExclusive: 2_500_000_000, mortgageCapKrw: 200_000_000 }
    ],
    ltvByZone: { regulated: 0.40, nonregulated: 0.70 },
    dsrBank: 0.40,
    dsrNonBank: 0.50,
    stressRatePct: 3.0,
    note: "수도권·규제지역 주택구입목적 주담대 절대한도를 시가 구간별 6/4/2억원으로 차등화. 생활안정자금, 중도금·이주비 등은 적용범위가 다를 수 있으며 실제 허용액은 LTV·DSR·기타 요건 중 더 엄격한 제약을 따릅니다.",
    sourceTitle: "금융위원회, 주택시장 안정화를 위한 대출수요 관리 방안",
    sourceUrl: "https://www.fsc.go.kr/no010101/85432",
    reaffirmedAt: "2026-08-13",
    reaffirmedSourceUrl: "https://www.fsc.go.kr/no040101?cnId=3053"
  }
];


export interface HousingPolicyEvent {
  id: string;
  title: string;
  announcedAt: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  importance: HousingPolicyImportance;
  direction: HousingPolicyDirection;
  channels: HousingPolicyChannel[];
  status: "confirmed" | "candidate";
  sourceTitle: string;
  sourceUrl: string;
  note: string;
}

/**
 * Confirmed minor policies live here as modifiers only. They must never be inserted into
 * HOUSING_POLICY_REGIMES because a minor event must not replace the active numerical regime.
 * Add an item only when its official effective date / scope is verified.
 */
export const HOUSING_POLICY_MODIFIERS: HousingPolicyEvent[] = [];

export function confirmedHousingPolicyEvents(): HousingPolicyEvent[] {
  const majorRegimes = HOUSING_POLICY_REGIMES.map((regime) => ({
    id: regime.id,
    title: regime.sourceTitle,
    announcedAt: regime.announcedAt ?? null,
    effectiveFrom: regime.effectiveFrom,
    effectiveTo: regime.effectiveTo ?? null,
    importance: "major" as const,
    direction: regime.direction,
    channels: regime.channels,
    status: "confirmed" as const,
    sourceTitle: regime.sourceTitle,
    sourceUrl: regime.sourceUrl,
    note: regime.note
  }));
  return [...majorRegimes, ...HOUSING_POLICY_MODIFIERS]
    .sort((a, b) => (a.effectiveFrom ?? a.announcedAt ?? "").localeCompare(b.effectiveFrom ?? b.announcedAt ?? ""));
}

function isoDateOnly(value: string) {
  const match = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (!match) throw new Error(`Housing policy date must be YYYY-MM-DD: ${value}`);
  return value;
}

export function regimeAppliesToRegion(regime: HousingPolicyRegime, region: HousingRegionProfile) {
  return (regime.appliesToCapitalArea && region.inCapitalArea) || (regime.appliesToRegulatedArea && region.regulatedArea);
}

export function resolveHousingPolicyRegime(date: string, region: HousingRegionProfile, loanPurpose: HousingLoanPurpose = "purchase") {
  const d = isoDateOnly(date);
  return [...HOUSING_POLICY_REGIMES]
    .filter((regime) => regime.loanPurpose === loanPurpose || regime.loanPurpose === "all")
    .filter((regime) => regimeAppliesToRegion(regime, region))
    .filter((regime) => regime.effectiveFrom <= d && (!regime.effectiveTo || d <= regime.effectiveTo))
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0] ?? null;
}

export function resolveHousingPriceBand(regime: HousingPolicyRegime, homePriceKrw: number) {
  if (!(homePriceKrw > 0)) return null;
  return regime.bands.find((band) => {
    const aboveMin = band.minPriceExclusive == null || homePriceKrw > band.minPriceExclusive;
    const belowMax = band.maxPriceInclusive == null || homePriceKrw <= band.maxPriceInclusive;
    return aboveMin && belowMax;
  }) ?? null;
}

function minKnown(values: Array<{ key: MortgageCapacityResult["bindingConstraint"]; value: number | null }>) {
  const known = values.filter((x): x is { key: Exclude<MortgageCapacityResult["bindingConstraint"], "multiple" | "unknown">; value: number } => x.value != null && Number.isFinite(x.value));
  if (known.length === 0) return { value: null, binding: "unknown" as const };
  const min = Math.min(...known.map((x) => x.value));
  const binders = known.filter((x) => Math.abs(x.value - min) < 1);
  return { value: min, binding: binders.length > 1 ? "multiple" as const : binders[0].key };
}

export function maxMortgageCapacity(input: MortgageCapacityInput): MortgageCapacityResult {
  const regime = resolveHousingPolicyRegime(input.date, input.region, input.loanPurpose ?? "purchase");
  if (!regime) {
    return {
      regimeId: null,
      bandId: null,
      bandLabel: null,
      homePriceKrw: input.homePriceKrw,
      absoluteCapKrw: null,
      ltv: null,
      ltvLimitKrw: null,
      dsrLimitKrw: input.dsrLimitKrw ?? null,
      allowedMortgageKrw: null,
      capacityRatio: null,
      equityRequiredKrw: null,
      bindingConstraint: "unknown",
      note: "해당 날짜·지역·용도에 대해 등록된 정책 레짐이 없어 대출가능액을 추정하지 않습니다."
    };
  }
  const band = resolveHousingPriceBand(regime, input.homePriceKrw);
  const absoluteCapKrw = band?.mortgageCapKrw ?? null;
  const ltv = input.region.regulatedArea ? regime.ltvByZone?.regulated ?? null : regime.ltvByZone?.nonregulated ?? null;
  const ltvLimitKrw = ltv == null ? null : input.homePriceKrw * ltv;
  const dsrLimitKrw = input.dsrLimitKrw ?? null;
  const constrained = minKnown([
    { key: "absolute_cap", value: absoluteCapKrw },
    { key: "ltv", value: ltvLimitKrw },
    { key: "dsr", value: dsrLimitKrw }
  ]);
  const allowedMortgageKrw = constrained.value == null ? null : Math.max(0, Math.min(input.homePriceKrw, constrained.value));
  return {
    regimeId: regime.id,
    bandId: band?.id ?? null,
    bandLabel: band?.label ?? null,
    homePriceKrw: input.homePriceKrw,
    absoluteCapKrw,
    ltv,
    ltvLimitKrw,
    dsrLimitKrw,
    allowedMortgageKrw,
    capacityRatio: allowedMortgageKrw == null ? null : allowedMortgageKrw / input.homePriceKrw,
    equityRequiredKrw: allowedMortgageKrw == null ? null : input.homePriceKrw - allowedMortgageKrw,
    bindingConstraint: constrained.binding,
    note: dsrLimitKrw == null
      ? "DSR 허용액은 차주별 소득·부채 정보가 없으므로 계산에서 제외했습니다. 표시값은 알려진 LTV와 가격대별 절대한도의 최소값이며 실제 대출 가능액의 상한 성격입니다."
      : "가격대별 절대한도, LTV, 입력된 DSR 허용액 중 가장 낮은 값을 적용했습니다."
  };
}

export function directRateSensitivityProxy(capacityRatio: number | null, mortgageRateChangePp: number | null) {
  if (capacityRatio == null || mortgageRateChangePp == null) return null;
  return capacityRatio * mortgageRateChangePp;
}

export function equityOpportunityCostAnnualKrw(equityRequiredKrw: number | null, benchmarkYieldPct: number | null) {
  if (equityRequiredKrw == null || benchmarkYieldPct == null) return null;
  return equityRequiredKrw * benchmarkYieldPct / 100;
}

export interface HousingCapacityPoint {
  priceEok: number;
  allowedMortgageEok: number | null;
  capacityPct: number | null;
  equityEok: number | null;
  bandId: string | null;
  bindingConstraint: MortgageCapacityResult["bindingConstraint"];
}

export function buildHousingCapacityCurve(args: {
  date: string;
  region: HousingRegionProfile;
  minPriceEok?: number;
  maxPriceEok?: number;
  stepEok?: number;
}) {
  const min = args.minPriceEok ?? 1;
  const max = args.maxPriceEok ?? 40;
  const step = args.stepEok ?? 0.5;
  const rows: HousingCapacityPoint[] = [];
  for (let priceEok = min; priceEok <= max + 1e-9; priceEok += step) {
    const normalized = Number(priceEok.toFixed(2));
    const result = maxMortgageCapacity({
      date: args.date,
      region: args.region,
      homePriceKrw: normalized * 100_000_000
    });
    rows.push({
      priceEok: normalized,
      allowedMortgageEok: result.allowedMortgageKrw == null ? null : result.allowedMortgageKrw / 100_000_000,
      capacityPct: result.capacityRatio == null ? null : result.capacityRatio * 100,
      equityEok: result.equityRequiredKrw == null ? null : result.equityRequiredKrw / 100_000_000,
      bandId: result.bandId,
      bindingConstraint: result.bindingConstraint
    });
  }
  return rows;
}

export function activeHousingPolicySnapshot(date: string) {
  return HOUSING_REGION_PROFILES.map((region) => {
    const regime = resolveHousingPolicyRegime(date, region);
    return {
      region,
      regime,
      curve: buildHousingCapacityCurve({ date, region })
    };
  });
}

export interface HousingPolicyBundle {
  asOfDate: string;
  regimes: HousingPolicyRegime[];
  policyBands: HousingPriceBand[];
  activeRegimeId: string | null;
  activeRegime: HousingPolicyRegime | null;
  profiles: ReturnType<typeof activeHousingPolicySnapshot>;
  examplePricesEok: number[];
  transactionModule: {
    status: "not_connected" | "backfill" | "ready" | "partial";
    note: string;
    rtms: HousingRtmsBundle | null;
  };
}

export function localIsoDate(date = new Date()) {
  // Housing policy effective dates are Korean calendar dates. Keep regime resolution
  // deterministic even when the dashboard is opened from another timezone.
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function buildHousingPolicyBundle(asOfDate = localIsoDate(), rtms: HousingRtmsBundle | null = null): HousingPolicyBundle {
  const profiles = activeHousingPolicySnapshot(asOfDate);
  const current = profiles.find((x) => x.region.id === "capital_regulated")?.regime
    ?? profiles.find((x) => x.regime)?.regime
    ?? null;
  return {
    asOfDate,
    regimes: HOUSING_POLICY_REGIMES,
    policyBands: current?.bands ?? [],
    activeRegimeId: current?.id ?? null,
    activeRegime: current,
    profiles,
    examplePricesEok: [10, 20, 30],
    transactionModule: rtms && rtms.status !== "not_collected"
      ? {
          status: rtms.collection.errors.length > 0 ? "partial" : (rtms.coverage.backfillComplete ? "ready" : "backfill"),
          note: rtms.coverage.backfillComplete
            ? "SDP가 수집·보관한 국토교통부 아파트 매매 실거래가를 가격대별로 집계합니다. 최근 신고월의 수정분도 SDP 중앙 수집기가 반영합니다."
            : `SDP의 국토교통부 RTMS 과거자료 backfill 진행 중 (${rtms.coverage.completedCombinations}/${rtms.coverage.expectedCombinations} 지역×월). 중앙 수집 완료 구간만 표시합니다.`,
          rtms
        }
      : {
          status: "not_connected",
          note: "SDP 중앙 RTMS 데이터가 아직 연결되지 않았습니다. 중앙 API 연결이 복구될 때까지 거래량·가격반응은 N/A입니다.",
          rtms: null
        }
  };
}
